import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return '-';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '-';
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(d);
}

export function formatDateTime(date: string | Date | null | undefined): string {
  if (!date) return '-';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '-';
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatNumber(n: number): string {
  return new Intl.NumberFormat('en-IN').format(n);
}

export function getStatusBadgeClass(status: string): "default" | "success" | "info" | "warning" | "draft" | "submitted" | "manager" | "cluster" | "approved" | "rejected" | "rejected-cluster" | "returned" | "danger" | null | undefined {
  switch (status) {
    case 'DRAFT': return 'draft';
    case 'PENDING_MANAGER_APPROVAL': return 'submitted';
    case 'MANAGER_APPROVED': return 'manager';
    case 'REJECTED_BY_MANAGER': return 'rejected';
    case 'APPROVED': return 'approved';
    case 'REJECTED_BY_CLUSTER_HEAD': return 'rejected-cluster';
    default: return 'default';
  }
}

export function getStatusLabel(status: string): string {
  switch (status) {
    case 'DRAFT': return 'Draft';
    case 'PENDING_MANAGER_APPROVAL': return 'Pending Manager Approval';
    case 'MANAGER_APPROVED': return 'Manager Approved - Pending Cluster Head';
    case 'REJECTED_BY_MANAGER': return 'Rejected by Manager';
    case 'APPROVED': return 'Approved';
    case 'REJECTED_BY_CLUSTER_HEAD': return 'Rejected by Cluster Head';
    default: return status.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
  }
}

export function truncate(str: string, len: number): string {
  if (str.length <= len) return str;
  return str.slice(0, len) + '...';
}

/**
 * Inclusive calendar range of the current month computed from IST (UTC+5:30)
 * local date components, never UTC calendar dates. Mirrors the worker's
 * dashboard default so the filter bar shows the same range the API defaults to.
 */
export function currentMonthRangeIST(): { start: string; end: string } {
  const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
  const ist = new Date(Date.now() + IST_OFFSET_MS);
  const year = ist.getUTCFullYear();
  const month = ist.getUTCMonth();
  const nextYear = month === 11 ? year + 1 : year;
  const nextMonth = month === 11 ? 0 : month + 1;
  const endExclusive = new Date(Date.UTC(nextYear, nextMonth, 1));
  endExclusive.setUTCDate(0);
  const pad2 = (n: number) => n.toString().padStart(2, '0');
  return {
    start: `${year}-${pad2(month + 1)}-01`,
    end: `${endExclusive.getUTCFullYear()}-${pad2(endExclusive.getUTCMonth() + 1)}-${pad2(endExclusive.getUTCDate())}`,
  };
}
